import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import medicalExamCatalogService from '../../../services/medicalExamCatalogService';
import Icon from '../../common/Icons';
import {
  ANALYSIS_FAMILIES,
  MEDICAL_EXAM_CATEGORIES,
  buildEmptyExamForm,
  getMedicalExamCategoryConfig,
  MEDICAL_COVERAGE_TYPES
} from '../../../constants/medicalExamCategories';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import ExamCatalogListItem from './ExamCatalogListItem';
import '../HeadDoctorDashboard.css';

const COVERAGE_PILL_LABEL = {
  SMIE: 'SMIE',
  CMC: 'CMC'
};

/**
 * Gestion du catalogue d'examens (analyses, radiographies, échographies).
 *
 * @param {{ categoryId: 'ANALYSIS' | 'RADIOGRAPHY' | 'ULTRASOUND' }} props
 */
export default function MedicalExamCatalogManager({ categoryId }) {
  const categoryConfig = useMemo(
    () => getMedicalExamCategoryConfig(categoryId),
    [categoryId]
  );
  const copy = HEAD_DOCTOR_COPY.EXAM_CATALOG;
  const isAnalysis = categoryId === MEDICAL_EXAM_CATEGORIES.ANALYSIS.id;

  const { toast } = useDoctorWorkspace();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [items, setItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedCoverageType, setSelectedCoverageType] = useState(MEDICAL_COVERAGE_TYPES.SMIE.id);
  const [selectedAnalysisFamily, setSelectedAnalysisFamily] = useState('');
  const [form, setForm] = useState(() => buildEmptyExamForm(categoryId));
  const [editingId, setEditingId] = useState(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });

  const loadItems = useCallback(async () => {
    if (!categoryConfig) return;

    setLoading(true);
    setError('');
    try {
      const params = { category: categoryId, coverage_type: selectedCoverageType };
      if (selectedAnalysisFamily) params.analysis_family = selectedAnalysisFamily;
      if (search.trim()) params.search = search.trim();
      if (!showInactive) params.is_active = 'true';

      const res = await medicalExamCatalogService.getExams(params);
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Chargement impossible');
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [categoryConfig, categoryId, search, showInactive, selectedCoverageType, selectedAnalysisFamily]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const stats = useMemo(
    () => ({
      count: items.length,
      active: items.filter((i) => i.is_active).length,
      inactive: items.filter((i) => !i.is_active).length
    }),
    [items]
  );

  const hasActiveFilters =
    Boolean(searchInput.trim()) ||
    Boolean(selectedAnalysisFamily) ||
    showInactive ||
    selectedCoverageType !== MEDICAL_COVERAGE_TYPES.SMIE.id;

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setSelectedAnalysisFamily('');
    setShowInactive(false);
    setSelectedCoverageType(MEDICAL_COVERAGE_TYPES.SMIE.id);
  };

  const openActionMenu = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 220,
        menuHeight: estimateActionMenuHeight(3, { withSeparator: true })
      })
    );
    setActionMenuItem(item);
  };

  const closeActionMenu = () => setActionMenuItem(null);

  const resetForm = () => {
    setForm(buildEmptyExamForm(categoryId));
    setEditingId(null);
  };

  const openCreate = () => {
    closeActionMenu();
    resetForm();
    setModalError('');
    setShowEditorModal(true);
  };

  const startEdit = (item) => {
    closeActionMenu();
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      description: item.description || '',
      coverage_type: item.coverage_type || MEDICAL_COVERAGE_TYPES.SMIE.id,
      analysis_family: item.analysis_family || '',
      is_active: Boolean(item.is_active),
      metadata: {
        ...buildEmptyExamForm(categoryId).metadata,
        ...(item.metadata || {})
      }
    });
    setModalError('');
    setShowEditorModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowEditorModal(false);
    setModalError('');
    resetForm();
  };

  const updateMetadataField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [key]: value }
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!categoryConfig) return;

    setSaving(true);
    setModalError('');
    try {
      const payload = {
        category: categoryId,
        coverage_type: form.coverage_type,
        analysis_family: isAnalysis ? form.analysis_family : null,
        name: form.name.trim(),
        description: form.description.trim() || null,
        is_active: Boolean(form.is_active),
        metadata: form.metadata
      };

      if (!payload.name) throw new Error('Le libellé est obligatoire.');
      if (isAnalysis && !payload.analysis_family) {
        throw new Error('Le domaine de biologie est obligatoire.');
      }

      if (editingId) {
        await medicalExamCatalogService.updateExam(editingId, payload);
        toast('success', `${categoryConfig.label} mise à jour.`);
      } else {
        await medicalExamCatalogService.createExam(payload);
        toast('success', `${categoryConfig.label} ajoutée au catalogue.`);
      }

      closeModal();
      await loadItems();
    } catch (e) {
      setModalError(e.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.name} » du référentiel ?`)) return;
    closeActionMenu();
    try {
      await medicalExamCatalogService.deleteExam(item.id);
      toast('success', 'Entrée supprimée.');
      if (editingId === item.id) closeModal();
      await loadItems();
    } catch (e) {
      setError(e.message || 'Suppression impossible');
    }
  };

  const toggleActive = async (item) => {
    closeActionMenu();
    try {
      await medicalExamCatalogService.updateExam(item.id, {
        category: item.category,
        coverage_type: item.coverage_type || MEDICAL_COVERAGE_TYPES.SMIE.id,
        analysis_family: item.analysis_family || null,
        name: item.name,
        description: item.description,
        metadata: item.metadata || {},
        is_active: !item.is_active
      });
      toast('success', item.is_active ? 'Entrée désactivée.' : 'Entrée réactivée.');
      await loadItems();
    } catch (e) {
      setError(e.message || 'Mise à jour impossible');
    }
  };

  const renderCoveragePill = (coverageType) => {
    const isCmc = coverageType === MEDICAL_COVERAGE_TYPES.CMC.id;
    return (
      <span
        className={`hd-exam-coverage-pill hd-exam-coverage-pill--${
          isCmc ? 'cmc' : 'smie'
        }`}
      >
        {COVERAGE_PILL_LABEL[coverageType] || coverageType}
      </span>
    );
  };

  const isEditing = Boolean(editingId);
  const showPreview = Boolean(form.name.trim());

  if (!categoryConfig) {
    return (
      <Alert variant="danger" className="m-3">
        Catégorie d&apos;examen inconnue : {categoryId}
      </Alert>
    );
  }

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-exam-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={categoryConfig.pageTitle}
        description={categoryConfig.pageSubtitle}
        actions={
          <button type="button" className="hd-co-create-btn" onClick={openCreate}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <div className="hd-co-shell">
        <section className="hd-co-stats hd-co-stats--3" aria-label="Synthèse du catalogue">
          <article className="hd-co-stat-card">
            <span className="hd-co-stat-card__label">{copy.STAT_SHOWN}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.count}</span>
            <span className="hd-co-stat-card__hint">{copy.RESULTS_LABEL}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--success">
            <span className="hd-co-stat-card__label">{copy.STAT_ACTIVE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.active}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--queue">
            <span className="hd-co-stat-card__label">{copy.STAT_INACTIVE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.inactive}</span>
          </article>
        </section>

        <div className="hd-co-toolbar">
          <div className="hd-co-search-wrap">
            <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
            <input
              type="search"
              className="hd-co-search-input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_PLACEHOLDER}
            />
          </div>
          <div className="hd-co-toolbar-end">
            <div className="hd-co-filters" role="group" aria-label={copy.FILTER_COVERAGE}>
              {Object.values(MEDICAL_COVERAGE_TYPES).map((coverage) => (
                <button
                  key={coverage.id}
                  type="button"
                  className={`hd-co-filter-pill${
                    selectedCoverageType === coverage.id ? ' is-active' : ''
                  }`}
                  onClick={() => setSelectedCoverageType(coverage.id)}
                >
                  {COVERAGE_PILL_LABEL[coverage.id] || coverage.id}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="hd-co-refresh-btn"
              onClick={loadItems}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={18} aria-hidden />
            </button>
            <div className="hd-co-toolbar-meta">
              <strong>{loading ? '—' : stats.count}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="hd-exam-filter-bar">
          {isAnalysis ? (
            <div className="hd-exam-family-bar">
              <span className="hd-exam-family-bar__label" id="hd-exam-family-filter-label">
                {copy.FILTER_FAMILY}
              </span>
              <div
                className="hd-exam-family-bar__track"
                role="group"
                aria-labelledby="hd-exam-family-filter-label"
              >
                <button
                  type="button"
                  className={`hd-exam-family-pill-btn${
                    !selectedAnalysisFamily ? ' is-active' : ''
                  }`}
                  onClick={() => setSelectedAnalysisFamily('')}
                  aria-pressed={!selectedAnalysisFamily}
                >
                  {copy.FILTER_FAMILY_ALL_SHORT}
                </button>
                {Object.values(ANALYSIS_FAMILIES).map((family) => (
                  <button
                    key={family.id}
                    type="button"
                    className={`hd-exam-family-pill-btn${
                      selectedAnalysisFamily === family.id ? ' is-active' : ''
                    }`}
                    onClick={() => setSelectedAnalysisFamily(family.id)}
                    aria-pressed={selectedAnalysisFamily === family.id}
                  >
                    {family.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="hd-exam-filter-bar__end">
            <label className="hd-exam-inactive-toggle">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>{copy.FILTER_INACTIVE}</span>
            </label>
            {hasActiveFilters ? (
              <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
                {copy.FILTER_RESET}
              </button>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

        {loading ? (
          <div className="hd-co-state" role="status" aria-live="polite">
            <Spinner animation="border" className="hd-co-state__spinner" />
            <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="hd-co-state hd-co-state--empty">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="chartBar" size={32} />
            </div>
            <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
            <p className="hd-co-state__text">{copy.EMPTY}</p>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        ) : (
          <ul className="hd-exam-list" aria-label={copy.COL_EXAM}>
            {items.map((item) => (
              <ExamCatalogListItem
                key={item.id}
                item={item}
                categoryConfig={categoryConfig}
                isAnalysis={isAnalysis}
                selectedAnalysisFamily={selectedAnalysisFamily}
                isEditing={showEditorModal && editingId === item.id}
                copy={copy}
                onOpenMenu={(e) => openActionMenu(e, item)}
              />
            ))}
          </ul>
        )}
      </div>

      {actionMenuItem ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => startEdit(actionMenuItem)}
            >
              <Icon name="edit" size={15} />
              <span>{copy.ACTION_EDIT}</span>
            </button>
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => toggleActive(actionMenuItem)}
            >
              <Icon
                name={actionMenuItem.is_active ? 'eyeSlash' : 'checkCircle'}
                size={15}
              />
              <span>
                {actionMenuItem.is_active ? copy.ACTION_DEACTIVATE : copy.ACTION_ACTIVATE}
              </span>
            </button>
            <div className="hd-exam-actions-menu__separator" />
            <button
              type="button"
              className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
              role="menuitem"
              onClick={() => remove(actionMenuItem)}
            >
              <Icon name="trash" size={15} />
              <span>{copy.ACTION_DELETE}</span>
            </button>
          </div>
        </>
      ) : null}

      <Modal
        show={showEditorModal}
        onHide={closeModal}
        centered
        className="hd-dis-modal-root hd-exam-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog"
        contentClassName="hd-dis-modal__content"
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="chartBar" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <span
                className={`hd-dis-modal__mode${
                  isEditing ? ' hd-dis-modal__mode--edit' : ' hd-dis-modal__mode--new'
                }`}
              >
                {isEditing ? copy.MODAL_MODE_EDIT : copy.MODAL_MODE_NEW}
              </span>
              <h2 className="hd-dis-modal__title">
                {isEditing ? copy.MODAL_TITLE_EDIT : copy.MODAL_TITLE_NEW}
              </h2>
              <p className="hd-dis-modal__subtitle mb-0">
                {isEditing ? copy.MODAL_SUBTITLE_EDIT : copy.MODAL_SUBTITLE_NEW}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={closeModal}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>

        <Form onSubmit={submit}>
          <div className="hd-dis-modal__body">
            {modalError ? (
              <Alert variant="danger" className="hd-dis-modal__alert mb-0">
                {modalError}
              </Alert>
            ) : null}

            <div className="hd-dis-modal__fields">
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-exam-name">
                  {copy.FIELD_LABEL}
                </label>
                <input
                  id="hd-exam-name"
                  type="text"
                  className="hd-dis-modal__input"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={copy.FIELD_LABEL_PLACEHOLDER}
                  maxLength={255}
                  required
                  autoFocus
                  autoComplete="off"
                />
              </div>

              <div className="hd-dis-modal__field-grid">
                <div className="hd-dis-modal__field">
                  <label className="hd-dis-modal__label" htmlFor="hd-exam-coverage">
                    {copy.FIELD_COVERAGE}
                  </label>
                  <select
                    id="hd-exam-coverage"
                    className="hd-dis-modal__input hd-dis-modal__select"
                    value={form.coverage_type}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, coverage_type: e.target.value }))
                    }
                  >
                    {Object.values(MEDICAL_COVERAGE_TYPES).map((coverage) => (
                      <option key={coverage.id} value={coverage.id}>
                        {coverage.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isAnalysis ? (
                  <div className="hd-dis-modal__field">
                    <label className="hd-dis-modal__label" htmlFor="hd-exam-family">
                      {copy.FIELD_FAMILY}
                    </label>
                    <select
                      id="hd-exam-family"
                      className="hd-dis-modal__input hd-dis-modal__select"
                      value={form.analysis_family || ''}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, analysis_family: e.target.value }))
                      }
                      required
                    >
                      <option value="">{copy.FIELD_FAMILY_PLACEHOLDER}</option>
                      {Object.values(ANALYSIS_FAMILIES).map((family) => (
                        <option key={family.id} value={family.id}>
                          {family.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>

              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-exam-description">
                  {copy.FIELD_DESCRIPTION}
                </label>
                <textarea
                  id="hd-exam-description"
                  className="hd-dis-modal__textarea"
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder={copy.FIELD_DESCRIPTION_PLACEHOLDER}
                />
              </div>

              {categoryConfig.metadataFields.map((field) => (
                <div className="hd-dis-modal__field" key={field.key}>
                  {field.type === 'boolean' ? (
                    <label className="hd-exam-inactive-toggle hd-exam-inactive-toggle--modal">
                      <input
                        type="checkbox"
                        checked={Boolean(form.metadata[field.key])}
                        onChange={(e) => updateMetadataField(field.key, e.target.checked)}
                      />
                      <span>{field.label}</span>
                    </label>
                  ) : (
                    <>
                      <label className="hd-dis-modal__label" htmlFor={`hd-exam-meta-${field.key}`}>
                        {field.label}
                      </label>
                      <input
                        id={`hd-exam-meta-${field.key}`}
                        type="text"
                        className="hd-dis-modal__input"
                        value={form.metadata[field.key] || ''}
                        onChange={(e) => updateMetadataField(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        maxLength={field.maxLength}
                      />
                    </>
                  )}
                </div>
              ))}

              <label className="hd-exam-inactive-toggle hd-exam-inactive-toggle--modal">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
                <span>{copy.FIELD_ACTIVE}</span>
              </label>
            </div>

            {showPreview ? (
              <div className="hd-dis-modal__preview" aria-live="polite">
                <span className="hd-dis-modal__preview-label">{copy.MODAL_PREVIEW_LABEL}</span>
                <div className="hd-exam-modal-preview">
                  <div className="hd-exam-modal-preview__row">
                    {renderCoveragePill(form.coverage_type)}
                    {isAnalysis && form.analysis_family ? (
                      <span className="hd-exam-family-pill">
                        {ANALYSIS_FAMILIES[form.analysis_family]?.label || form.analysis_family}
                      </span>
                    ) : null}
                    <span
                      className={`hd-exam-status-pill ${
                        form.is_active
                          ? 'hd-exam-status-pill--active'
                          : 'hd-exam-status-pill--inactive'
                      }`}
                    >
                      <span className="hd-exam-status-pill__dot" />
                      {form.is_active ? copy.STATUS_ACTIVE : copy.STATUS_INACTIVE}
                    </span>
                  </div>
                  <span className="hd-exam-label">{form.name.trim()}</span>
                  {form.description.trim() ? (
                    <span className="hd-exam-desc">{form.description.trim()}</span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="hd-dis-modal__footer">
            <button
              type="button"
              className="hd-dis-modal__btn-cancel"
              onClick={closeModal}
              disabled={saving}
            >
              {copy.BTN_CANCEL}
            </button>
            <button type="submit" className="hd-co-create-btn" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  {copy.BTN_SAVE}
                </>
              ) : isEditing ? (
                copy.BTN_UPDATE
              ) : (
                <>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.BTN_CREATE}
                </>
              )}
            </button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
